import { createApi } from '@reduxjs/toolkit/query/react';
import { graphqlRequestBaseQuery } from '@rtk-query/graphql-request-base-query';
import { client } from 'graphql/client';
import { getActiveMissionRouteV2, getActiveMissionV2 } from 'graphql/queries';
import { onUpdateActiveMissionByVin } from 'graphql/subscriptions';
import { SafeMissionV2Schema } from 'helper/v2Schemas';

import { AppError } from './errors';
import { fetchV2 } from './utils';

import type { GeneratedRouteV2, GetActiveMissionRouteV2Query, GetActiveMissionV2Query, MissionV2 } from 'API';
import { GeneratedRouteV2Schema } from 'APIzod';

const extractResult = <T>(
    result: { __typename?: string; errorCode?: string; statusCode?: number } | null | undefined,
    notFoundMessage: string
): T => {
    const isError = !result || result.__typename === 'ErrorResponse' || result.errorCode !== undefined;
    if (isError) {
        throw new AppError(result?.errorCode ?? notFoundMessage, {
            errorCode: result?.errorCode,
            statusCode: result?.statusCode,
        });
    }
    return result as unknown as T;
};

const parseMission = (data: MissionV2) => SafeMissionV2Schema().parse(data);

const getMissionV3QueryFn = (vin: string) =>
    fetchV2(
        () => client.graphql({ query: getActiveMissionV2, variables: { vin } }),
        (data: GetActiveMissionV2Query) =>
            data?.getActiveMissionV2 != null
                ? parseMission(extractResult<MissionV2>(data.getActiveMissionV2, 'Mission not found'))
                : null,
        '[getMissionV3QueryFn]: Unexpected error occurred while fetching V3 mission'
    );

const getActiveMissionRoute = (vin: string) =>
    fetchV2(
        () => client.graphql({ query: getActiveMissionRouteV2, variables: { vin } }),
        (data: GetActiveMissionRouteV2Query) =>
            data?.getActiveMissionRouteV2 != null
                ? GeneratedRouteV2Schema().parse(
                      extractResult<GeneratedRouteV2>(data.getActiveMissionRouteV2, 'Mission route not found')
                  )
                : null,
        '[getActiveMissionRoute]: Unexpected error occurred while fetching the active mission route'
    );

const withCachedRoute = (mission: MissionV2, cached: MissionV2 | null | undefined): MissionV2 =>
    mission.route || cached?.id !== mission.id ? mission : { ...mission, route: cached.route };

export const missionV3Api = createApi({
    reducerPath: 'missionV3',
    baseQuery: graphqlRequestBaseQuery({ url: '/graphql' }),
    refetchOnMountOrArgChange: true,
    endpoints: builder => ({
        getMissionV3: builder.query<MissionV2 | null, string>({
            queryFn: getMissionV3QueryFn,
        }),
        onMissionV3Subscription: builder.query<MissionV2 | null, string>({
            queryFn: getMissionV3QueryFn,
            onCacheEntryAdded: async (vin, { cacheDataLoaded, cacheEntryRemoved, getCacheEntry, updateCachedData }) => {
                // Waiting for a baseline before streaming updates is intentional and is RTK Query's
                // intended contract. Do not remove it without reading the rest of this comment.
                // `cacheDataLoaded` resolves only on a fulfilled query, so while the query keeps failing
                // no subscription is opened and this vehicle receives no live updates. That wait is not
                // abandoned though: it resolves on the FIRST successful fetch for this cache key, so a
                // later refetch (this api sets refetchOnMountOrArgChange, so a remount is enough) opens
                // the subscription at that point.
                //
                // TODO: REST-SUBSCRIPTIONS: two things are missing behind that wait.
                // 1. Nothing retries on its own. There is no pollingInterval, no refetchOnReconnect and
                //    no refetchOnFocus, so within a single mount a failed query never recovers and the
                //    user has to navigate away and back. Add a retry or a reconnect refetch.
                // 2. Pushes carry the whole mission EXCEPT its route: the route can exceed the SNS message
                //    size limit and never changes after creation, so the backend leaves it out. The route
                //    therefore only comes from the initial query or, for a newly assigned mission, from
                //    getActiveMissionRouteV2. If the replacement transport also sends snapshots without
                //    the route, keep that split; otherwise the newest message can simply win.
                // The user-visible half of this is already handled: useGetMissionData returns `currentData`
                // so a failed load no longer serves the previous vehicle's mission, and VehiclePage
                // forwards the error to an ErrorBoundary so it is reported instead of being dressed up as
                // "No mission assigned". What remains is purely the recovery behaviour above.
                // To verify: force the first mission request to fail and confirm the screen reports the
                // failure, then let a retry succeed and confirm live updates start flowing.
                await cacheDataLoaded;
                if (import.meta.env.DEV) {
                    await cacheEntryRemoved;
                } else {
                    let routeRequestedFor: string | undefined;
                    const fetchMissingRoute = async (missionId: string) => {
                        if (routeRequestedFor === missionId) return;
                        routeRequestedFor = missionId;
                        try {
                            const { data: route } = await getActiveMissionRoute(vin);
                            if (!route) return;
                            updateCachedData(draft => {
                                if (draft?.id === missionId && !draft.route) draft.route = route;
                            });
                        } catch {
                            // Already logged by fetchV2. Allow a later push to retry.
                            routeRequestedFor = undefined;
                        }
                    };

                    const connection = client
                        .graphql({ query: onUpdateActiveMissionByVin, variables: { vin } })
                        .subscribe({
                            next: ({ data }) => {
                                if (!data.onUpdateActiveMissionByVin) {
                                    console.error(
                                        '[onMissionV3Subscription]: Please make sure all the attributes are returned in the mutation'
                                    );
                                    return;
                                }
                                const payload = data.onUpdateActiveMissionByVin.payload;
                                const hasMission = 'id' in payload && payload.id != null;
                                if (!hasMission) {
                                    updateCachedData(() => null);
                                    return;
                                }
                                // TODO: REST-SUBSCRIPTIONS: a bad payload makes `parseMission` throw here, so the
                                // update is dropped without anyone noticing: the screen silently keeps showing the
                                // previous mission and its old event log.
                                // How to fix when subscriptions move off GraphQL:
                                // 1. Use `safeParse` (or wrap `parseMission` in try/catch) so a bad payload
                                //    gives you a result object instead of an exception.
                                // 2. Only call `updateCachedData` when parsing worked. If it failed, log it and
                                //    leave the cache alone on purpose, so it is a decision and not an accident.
                                // 3. Also tell the UI the data is stale, otherwise the user keeps reading old
                                //    values as if they were live.
                                // 4. To verify: send one push with a broken field and check the app keeps
                                //    working and you can see in the console that the push was rejected.
                                const mission = withCachedRoute(
                                    parseMission(extractResult<MissionV2>(payload, 'Mission not found')),
                                    getCacheEntry().data
                                );
                                updateCachedData(() => mission);
                                // `void` = intentionally not awaited: show the mission now, patch the route in
                                // when it arrives. Safe to drop the promise because fetchMissingRoute never rejects.
                                if (!mission.route) void fetchMissingRoute(mission.id);
                            },
                            error: error => console.warn(error),
                        });
                    cacheEntryRemoved
                        .then(() => {
                            connection.unsubscribe();
                        })
                        .catch(error => console.error(error));
                }
            },
        }),
    }),
});

export const { useOnMissionV3SubscriptionQuery } = missionV3Api;
